import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';

import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { OperationStatus } from 'src/api/wa/store/models/workplace/OperationStatus';
import { WaScreensMap } from '../../router/WardsScreenMap';
import { WA_Screen } from '../../router/WardsScreens';
import { wardsNavigate } from '../../router/WardsScreenRegistration';
import useWaSecurityService from 'src/api/wa/service/WaSecurityService';
import useWaRegionService from 'src/api/wa/service/WaRegionService';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useWaLocationService } from 'src/api/wa/service/WaLocationService';
import LinkButton, { LinkButtonProps } from '../../common/button/LinkButton';

function S1_2_RecoverySupportMenu() {
  const { getDisasters } = useWaRegionService();
  const { waLogoutUser, waGetUser } = useWaSecurityService();

  const { user } = useWaUserStore();
  const { updateWaUserLocation } = useWaLocationService();

  const hasUserUpdatedRef = useRef(false);

  const { selectedDisaster } = useWaDisasterStore();
  const {
    patchWorkplaceFilterParameters,
    setFilterChanged,
    filtersInitialized,
    setFiltersInitialized,
    setCustomerBranchCode,
  } = useWaFilterStore();

  useEffect(() => {
    if (!user || hasUserUpdatedRef.current) return;

    hasUserUpdatedRef.current = true;
    updateWaUserLocation();
  }, [user, updateWaUserLocation]);

  useEffect(() => {
    if (!user?.branchCode || filtersInitialized) return;

    patchWorkplaceFilterParameters({
      reserved: false,
      branchCode: user.branchCode,
      salesOfficeCode: user.salesOfficeCode,
      sectionCode: user.sectionCode,
      squadCode: user.squadCode,
      statusList: OperationStatus.Incomplete,
      customerId: '',
    });
    setCustomerBranchCode(user.branchCode);
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    patchWorkplaceFilterParameters,
    setCustomerBranchCode,
    setFiltersInitialized,
    user,
  ]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToUnaddressedMap = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplacesMap].path);
  };

  const navigateToUnaddressedList = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.UnadressedWorkplacesList].path);
  };

  const navigateToAddressedList = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.AddressedWorkplacesList].path);
  };

  const navigateToEntrapedList = () => {
    patchWorkplaceFilterParameters({
      statusList: OperationStatus.Entrapment,
      customerId: '',
      reserved: undefined,
    });
    setFilterChanged(false);
    wardsNavigate(navigate, WaScreensMap[WA_Screen.UnadressedWorkplacesList].path);
  };

  const logoutUser = () => {
    waLogoutUser();
  };

  const navigateAggregateResults = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.ProgressCheck].path);
  };

  const buttons: LinkButtonProps[] = [
    {
      title: `${t('wards.supportMenu.unaddressedPropertiesMap')}`,
      onClick: () => navigateToUnaddressedMap(),
      disabled: false,
    },
    {
      title: `${t('wards.supportMenu.unaddressedPropertiesList')}`,
      onClick: () => navigateToUnaddressedList(),
    },
    {
      title: `${t('wards.supportMenu.addressedPropertiesList')}`,
      onClick: () => navigateToAddressedList(),
    },
    {
      title: `${t('wards.supportMenu.entrappedPropertiesList')}`,
      onClick: () => navigateToEntrapedList(),
    },
    {
      title: `${t('wards.supportMenu.aggregateResults')}`,
      onClick: () => navigateAggregateResults(),
    },
    {
      title: `${t('wards.common.logout')}`,
      onClick: () => logoutUser(),
    },
  ];
  const lastButton = buttons[buttons.length - 1];

  const initialLoader = useCallback(async () => {
    waGetUser();
    getDisasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisasterLoading = selectedDisaster === undefined;
  const isDisasterActive = !!selectedDisaster && selectedDisaster.stopDateTime === null;

  return (
    <div className="flex min-h-screen flex-col items-center bg-gray-100 px-4 pt-8">
      <h2 className="mb-6 text-center text-2xl font-bold">{t('wards.supportMenu.mainTitle')}</h2>

      {/* ▼ 3状態に分岐 */}
      {isDisasterLoading ? (
        <div className="mb-8 flex justify-start ps-2 text-center">
          {t('wards.desktop.menu.loadingWideAreaDisaster')}
        </div>
      ) : isDisasterActive ? (
        <h3 className="mb-8 text-center text-xl font-bold">
          {`${t('wards.supportMenu.disasterIdPlaceholder')} ${selectedDisaster?.id}`}
        </h3>
      ) : (
        <div className="mb-8 flex justify-start ps-2 text-center">
          {t('wards.desktop.menu.noWideAreaDisaster')}
        </div>
      )}

      <div className="w-full">
        <ul className="overflow-hidden rounded-2xl bg-white shadow">
          {/* ロード中 or 災害なし → ログアウトボタンのみ */}
          {isDisasterLoading || !isDisasterActive ? (
            <li className={`border-b border-gray-200  p-4 text-center`}>
              <LinkButton
                title={lastButton.title}
                {...(lastButton?.onClick && { onClick: lastButton.onClick })}
              />
            </li>
          ) : (
            buttons.map((item, index) => (
              <li
                key={index}
                className={`p-4 text-center ${index !== buttons.length - 1 ? 'border-b border-gray-200' : ''}`}
              >
                <LinkButton
                  title={item.title}
                  {...(item.onClick && { onClick: item.onClick })}
                  {...(item.disabled !== undefined && { disabled: item.disabled })}
                />
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default S1_2_RecoverySupportMenu;
